import { Box, InputBase, styled } from '@mui/material';
import { getZoomAdjustedPx } from 'components/ui/utilities/zoom';
import type { PDFDocumentSearchDensity } from './PDFDocumentSearch.props';

interface PdfSearchZoomStyleProps {
  $density?: PDFDocumentSearchDensity;
  $topOffset?: number;
  $zoomLevel: number;
}

const shouldForwardZoomProp = (prop: PropertyKey) =>
  prop !== '$zoomLevel' && prop !== '$density' && prop !== '$topOffset';

const getPdfSearchPx = (
  value: number,
  zoomLevel: number,
  density: PDFDocumentSearchDensity = 'regular',
  compactValue = value
) => getZoomAdjustedPx(density === 'compact' ? compactValue : value, zoomLevel);

export const SearchPanel = styled(Box, {
  shouldForwardProp: shouldForwardZoomProp,
})<PdfSearchZoomStyleProps>(({ $density, $topOffset = 0, $zoomLevel }) => ({
  position: 'absolute',
  top: getZoomAdjustedPx($topOffset, $zoomLevel),
  right: 0,
  bottom: 0,
  width: getPdfSearchPx(320, $zoomLevel, $density, 260),
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#f8f9fa',
  borderLeft: '1px solid #d0d0d0',
  boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
  zIndex: 10,
  overflow: 'hidden',
}));

export const SearchInputRow = styled(Box, {
  shouldForwardProp: shouldForwardZoomProp,
})<PdfSearchZoomStyleProps>(({ $density, $zoomLevel }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: getPdfSearchPx(6, $zoomLevel, $density, 4),
  padding: `${getPdfSearchPx(8, $zoomLevel, $density, 6)} ${getPdfSearchPx(10, $zoomLevel, $density, 8)}`,
  borderBottom: '1px solid #e0e0e0',
  backgroundColor: '#fff',
}));

export const StyledSearchInput = styled(InputBase, {
  shouldForwardProp: shouldForwardZoomProp,
})<PdfSearchZoomStyleProps>(({ $density, $zoomLevel }) => ({
  alignItems: 'center',
  flex: 1,
  fontSize: getPdfSearchPx(13, $zoomLevel, $density, 12),
  height: getPdfSearchPx(30, $zoomLevel, $density, 26),
  backgroundColor: '#fff',
  borderRadius: getPdfSearchPx(4, $zoomLevel, $density),
  border: '1px solid #d0d0d0',
  padding: `0 ${getPdfSearchPx(8, $zoomLevel, $density, 7)}`,
  minWidth: 0,
  '& .MuiInputBase-input': {
    boxSizing: 'border-box',
    fontSize: 'inherit',
    height: '100%',
    lineHeight: getPdfSearchPx(30, $zoomLevel, $density, 26),
    padding: 0,
  },
  '&.Mui-focused': {
    borderColor: '#1976d2',
    boxShadow: '0 0 0 1px rgba(25,118,210,0.2)',
  },
}));

export const SearchResultsContainer = styled(Box)({
  overflow: 'auto',
  flex: 1,
});

export const PageGroupHeader = styled(Box, {
  shouldForwardProp: shouldForwardZoomProp,
})<PdfSearchZoomStyleProps>(({ $density, $zoomLevel }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: getPdfSearchPx(4, $zoomLevel, $density, 3),
  padding: `${getPdfSearchPx(4, $zoomLevel, $density, 3)} ${getPdfSearchPx(6, $zoomLevel, $density, 5)} ${getPdfSearchPx(4, $zoomLevel, $density, 3)} ${getPdfSearchPx(4, $zoomLevel, $density, 3)}`,
  cursor: 'pointer',
  fontSize: getPdfSearchPx(12, $zoomLevel, $density, 11),
  fontWeight: 600,
  color: '#444',
  backgroundColor: '#eef1f5',
  borderBottom: '1px solid #e0e0e0',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  userSelect: 'none',
  '&:hover': {
    backgroundColor: '#e3e8ee',
  },
}));

export const PageGroupCount = styled('span', {
  shouldForwardProp: shouldForwardZoomProp,
})<PdfSearchZoomStyleProps>(({ $density, $zoomLevel }) => ({
  marginLeft: 'auto',
  fontSize: getPdfSearchPx(10, $zoomLevel, $density, 9),
  fontWeight: 400,
  color: '#888',
  backgroundColor: '#d8dce2',
  borderRadius: getPdfSearchPx(8, $zoomLevel, $density),
  padding: `${getPdfSearchPx(1, $zoomLevel, $density)} ${getPdfSearchPx(6, $zoomLevel, $density, 5)}`,
}));

export const SearchResultItem = styled(Box, {
  shouldForwardProp: shouldForwardZoomProp,
})<PdfSearchZoomStyleProps>(({ $density, $zoomLevel }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: getPdfSearchPx(6, $zoomLevel, $density, 4),
  padding: `${getPdfSearchPx(4, $zoomLevel, $density, 3)} ${getPdfSearchPx(8, $zoomLevel, $density, 6)} ${getPdfSearchPx(4, $zoomLevel, $density, 3)} ${getPdfSearchPx(24, $zoomLevel, $density, 18)}`,
  cursor: 'pointer',
  fontSize: getPdfSearchPx(12, $zoomLevel, $density, 11),
  lineHeight: 1.5,
  transition: 'background-color 0.1s',
  '&:hover': {
    backgroundColor: '#e8f0fe',
  },
  '&.active': {
    backgroundColor: '#cfe0fc',
  },
}));

export const ContextText = styled('span', {
  shouldForwardProp: shouldForwardZoomProp,
})<PdfSearchZoomStyleProps>(({ $density, $zoomLevel }) => ({
  color: '#666',
  fontSize: getPdfSearchPx(12, $zoomLevel, $density, 11),
  fontFamily: 'inherit',
  wordBreak: 'break-word',
}));

export const MatchText = styled('span', {
  shouldForwardProp: shouldForwardZoomProp,
})<PdfSearchZoomStyleProps>(({ $density, $zoomLevel }) => ({
  color: '#1a1a1a',
  backgroundColor: '#fff3b0',
  fontWeight: 600,
  fontSize: getPdfSearchPx(12, $zoomLevel, $density, 11),
  fontFamily: 'inherit',
  borderRadius: getPdfSearchPx(2, $zoomLevel, $density),
  padding: `0 ${getPdfSearchPx(1, $zoomLevel, $density)}`,
}));

export const ResultsSummary = styled(Box, {
  shouldForwardProp: shouldForwardZoomProp,
})<PdfSearchZoomStyleProps>(({ $density, $zoomLevel }) => ({
  padding: `${getPdfSearchPx(4, $zoomLevel, $density, 3)} ${getPdfSearchPx(10, $zoomLevel, $density, 8)}`,
  fontSize: getPdfSearchPx(11, $zoomLevel, $density, 10),
  color: '#888',
  borderBottom: '1px solid #e8e8e8',
  backgroundColor: '#f4f5f7',
  flexShrink: 0,
}));

export const NoResults = styled(Box, {
  shouldForwardProp: shouldForwardZoomProp,
})<PdfSearchZoomStyleProps>(({ $density, $zoomLevel }) => ({
  padding: `${getPdfSearchPx(20, $zoomLevel, $density, 14)} ${getPdfSearchPx(12, $zoomLevel, $density, 10)}`,
  fontSize: getPdfSearchPx(12, $zoomLevel, $density, 11),
  color: '#888',
  textAlign: 'center',
}));
